<template>
  <div>
    <h1 class="onboarding__title mb-5">
      {{ $t('onboarding.details.title') }}
    </h1>
    <p class="mb-10">
      {{ $t('onboarding.details.description') }}
    </p>

    <v-form class="mw-700 py-5 mx-auto" ref="realtyForm" v-model="valid">
      <v-row>
        <v-col>
          <AddressSearch
            @address="getAddressData"
            :rules="[rules.required]"
            id="google-address"
            ref="realtyAddress"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <v-text-field
            v-model="realty.code"
            :label="`${$t('realty.label.code')}*`"
            :rules="[rules.code, rules.required]"
            hide-details="auto"
            outlined
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model="realty.privateAreaMeters"
            :rules="validSize"
            :label="`${$t('realty.label.privateArea')}*`"
            class="input__textual-icon input__text-right"
            hide-details="auto"
            append-icon="m²"
            type="number"
            outlined
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <v-select
            v-model="realty.realty_type"
            :label="`${$t('realty.formCreate.realtyType')}*`"
            :rules="[rules.required]"
            :items="realtyTypes"
            hide-details="auto"
            item-value="value"
            item-text="name"
            outlined
          />
        </v-col>
        <v-col cols="6">
          <v-select
            v-model="furnishing"
            :label="$t('realty.label.furniture')"
            :items="furnishingOptions"
            hide-details="auto"
            item-value="value"
            item-text="name"
            outlined
          />
        </v-col>
      </v-row>

      <h4 class="mt-6 mb-2 grey--text text--darken-2">
        {{ $t('realty.label.theRealtyContains') }}
      </h4>
      <div class="d-flex mw-250">
        <HCheckbox
          :label="`${$t('realty.label.trousseau')}`"
          v-model="realty.trousseau"
        />
      </div>
    </v-form>

    <v-row class="mt-10" no-gutters>
      <v-col>
        <v-btn @click="setOnboardingStepsNavigation(3)" elevation="0" large>
          <v-icon left small> mdi-chevron-left </v-icon>
          {{ $t('onboarding.actions.previous') }}
        </v-btn>
      </v-col>
      <v-col class="text-right">
        <v-btn
          @click="handleUpdateDetails"
          :loading="isLoading"
          :disabled="!valid || !this.realty.city_id"
          color="primary"
          elevation="0"
          large
        >
          {{ $t('onboarding.actions.next') }}
          <v-icon right small> mdi-chevron-right </v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import HTextInput from '@/components/app/HTextInput'
import HCheckbox from '@/components/app/HCheckbox'
import AddressSearch from '@/components/app/AddressSearch'
import { deepEqual } from '@/util/helpers'
import { code, required } from '@/util/errorHandling'
import { FURNISHING, fromFurnishing, furnishingOptions, toFurnishing } from '@/util/furnishing'
import { realtyTypes } from '@/util/enums'

export default {
  name: 'OnboardingStepsFootage',

  components: {
    HTextInput,
    HCheckbox,
    AddressSearch,
  },

  data() {
    return {
      required: [(v) => !!v || this.$t('rules.required')],
      validSize: [
        (v) => !!v || this.$t('rules.required'),
        (v) => v > 0 || this.$t('rules.minValue', { value: 1 }),
        (v) => v <= 30000 || this.$t('rules.maxValue', { value: 30000 }),
      ],
      realty: {
        furnished: false,
        semi_furnished: false,
        trousseau: false,
        painting_status: 'HOST_CRITERIA',
        // Declared up front so Vue 2 tracks it; required to create the realty and prices a house
        realty_type: null,
      },
      address: '',
      valid: false,
      isUpdate: false,
      savedRealty: {},
      furnishingOptions: furnishingOptions((key) => this.$t(key)),
      realtyTypes,

      rules: {
        code,
        required,
      }
    }
  },

  mounted() {
    this.fetchCities()
    this.setUpdatedFields()
  },

  computed: {
    ...mapGetters([
      'isLoading',
      'onboardingContractId',
      'cities',
      'createRealtySuccess',
      'realties',
    ]),

    furnishing: {
      get() {
        return (
          toFurnishing(this.realty.furnished, this.realty.semi_furnished) ??
          FURNISHING.NOT_FURNISHED
        )
      },
      set(furnishing) {
        Object.assign(this.realty, fromFurnishing(furnishing))
      },
    },
  },

  methods: {
    ...mapActions([
      'calculateAppointmentValue',
      'fetchCities',
      'fetchRealties',
      'createRealty',
      'updateRealty',
    ]),
    ...mapMutations(['setOnboardingStepsNavigation', 'setIsLoading']),

    async create() {
      this.realty.squareMeters = this.realty.privateAreaMeters

      this.setIsLoading(true)
      await this.createRealty(this.realty)
        .then(async () => {
          if (this.createRealtySuccess) {
            await this.fetchRealties()
            this.calculateValue()
          }
        })
        .finally(() => this.setIsLoading(false))
    },
    async update() {
      this.setIsLoading(true)
      const requestBody = {
        id: this.realty.id,
        code: this.realty.code,
        address: this.realty.address,
        square_meters: this.realty.privateAreaMeters,
        private_area_meters: this.realty.privateAreaMeters,
        selectedCity: this.realty.city_id,
        trousseau: this.realty.trousseau,
        furnished: this.realty.furnished,
        semi_furnished: this.realty.semi_furnished,
        painting_status: this.realty.painting_status,
        realty_type: this.realty.realty_type,
      }
      await this.updateRealty(requestBody)
        .then(async () => {
          await this.fetchRealties()
          this.calculateValue()
        })
        .finally(() => this.setIsLoading(false))
    },
    handleUpdateDetails() {
      if (!this.$refs.realtyForm.validate()) return

      if (!this.isUpdate) {
        this.create()
      } else {
        if (!deepEqual(this.realty, this.savedRealty)) {
          this.update()
        } else {
          this.calculateValue()
        }
      }
    },
    calculateValue() {
      const request = {
        service_contract_id: this.onboardingContractId,
        furnished: this.realty.furnished,
        semi_furnished: this.realty.semi_furnished,
        trousseau: this.realty.trousseau,
        realty_type: this.realty.realty_type,
        private_area_meters: parseInt(this.realty.privateAreaMeters, 10),
      }

      if (request.service_contract_id && request.private_area_meters) {
        this.calculateAppointmentValue(request)
      }
    },
    getAddressData({ address, city }) {
      this.realty.address = address
      this.address = { city }
      this.updateCityBySelectedAddress()
    },
    updateCityBySelectedAddress() {
      let city = this.cities.find(
        (it) => it.name === this.address.city.toUpperCase()
      )

      if (city) {
        this.realty.city_id = city.id
      }
    },
    setUpdatedFields() {
      this.fetchRealties()
      if (this.realties.length) {
        this.isUpdate = true
        const savedRealty = this.realties[0]
        this.realty.code = savedRealty.code.toString()
        this.realty.furnished = savedRealty.furnished
        this.realty.semi_furnished = savedRealty.semi_furnished ?? false
        this.realty.trousseau = savedRealty.trousseau
        this.realty.realty_type = savedRealty.realty_type
        this.realty.privateAreaMeters = savedRealty.private_area_meters.toString()
        this.realty.squareMeters = this.realty.privateAreaMeters
        this.realty.address = savedRealty.address
        this.realty.city_id = savedRealty.city.id
        this.realty.id = savedRealty.id
        this.savedRealty = { ...this.realty }
        this.$refs.realtyAddress.googleAddress.address = this.realty.address
      }
    },
  },
}
</script>
